import type { UploadData } from '@/lib/processUpload';

const DATABASE_NAME = 'fortuna-upload-retries';
const STORE_NAME = 'uploads';
const DATABASE_VERSION = 1;

export interface UploadRetryRecord {
  uploadId: string;
  userId: string;
  data: UploadData;
  createdAt: number;
  updatedAt: number;
}

function openDatabase(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    if (typeof indexedDB === 'undefined') {
      reject(new Error('IndexedDB is not available in this browser.'));
      return;
    }

    const request = indexedDB.open(DATABASE_NAME, DATABASE_VERSION);
    request.onerror = () => reject(request.error ?? new Error('Unable to open retry storage.'));
    request.onsuccess = () => resolve(request.result);
    request.onupgradeneeded = () => {
      const database = request.result;
      if (!database.objectStoreNames.contains(STORE_NAME)) {
        database.createObjectStore(STORE_NAME, { keyPath: 'uploadId' });
      }
    };
  });
}

export async function saveUploadRetry(record: UploadRetryRecord): Promise<void> {
  const database = await openDatabase();
  await new Promise<void>((resolve, reject) => {
    const transaction = database.transaction(STORE_NAME, 'readwrite');
    transaction.objectStore(STORE_NAME).put({ ...record, updatedAt: Date.now() });
    transaction.onerror = () => reject(transaction.error ?? new Error('Unable to save retry data.'));
    transaction.oncomplete = () => resolve();
  });
  database.close();
}

export async function updateUploadRetry(
  uploadId: string,
  updates: Omit<Partial<UploadRetryRecord>, 'data'> & { data?: Partial<UploadData> },
): Promise<void> {
  const existing = await getUploadRetry(uploadId);
  if (!existing) return;

  await saveUploadRetry({
    ...existing,
    ...updates,
    data: { ...existing.data, ...updates.data },
    updatedAt: Date.now(),
  });
}

export async function getUploadRetry(uploadId: string): Promise<UploadRetryRecord | undefined> {
  const database = await openDatabase();
  const record = await new Promise<UploadRetryRecord | undefined>((resolve, reject) => {
    const request = database.transaction(STORE_NAME, 'readonly').objectStore(STORE_NAME).get(uploadId);
    request.onerror = () => reject(request.error ?? new Error('Unable to read retry data.'));
    request.onsuccess = () => resolve(request.result as UploadRetryRecord | undefined);
  });
  database.close();
  return record;
}

export async function deleteUploadRetry(uploadId: string): Promise<void> {
  const database = await openDatabase();
  await new Promise<void>((resolve, reject) => {
    const transaction = database.transaction(STORE_NAME, 'readwrite');
    transaction.objectStore(STORE_NAME).delete(uploadId);
    transaction.onerror = () => reject(transaction.error ?? new Error('Unable to delete retry data.'));
    transaction.oncomplete = () => resolve();
  });
  database.close();
}
